import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ExtensionFormRequest } from '../../../models/interface/app-download.interface';

import { createExtensionSchema } from '../../../validation/extension.schema';

interface UseExtensionBoxFormProps {
  initialEmailOptions?: { value: string; label: string }[];
  defaultValues?: Omit<ExtensionFormRequest,'serialNumber' | 'hardwareSignature'>;
}

export const useExtensionBoxForm = ({
  initialEmailOptions = [],
  defaultValues,
}: UseExtensionBoxFormProps) => {
  const [emailOptions, setEmailOptions] = useState(
    [
      {
        value: defaultValues?.agencyEmail || '',
        label: defaultValues?.agencyEmail || '',
      },
      ...(defaultValues?.userContactEmail
        ? [
            {
              value: defaultValues.userContactEmail,
              label: defaultValues.userContactEmail,
            },
          ]
        : []),
      ...initialEmailOptions,
    ]
      .filter((option) => option.value)
      .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i)
  );

  const extensionSchema = useMemo(
    () => createExtensionSchema(initialEmailOptions),
    [initialEmailOptions]
  );

  const {
    control,
    formState: { errors },
    watch,
    trigger,
    setValue,
  } = useForm({
    resolver: yupResolver(extensionSchema),
    mode: 'onChange',
    defaultValues: {
      notificationEmail: defaultValues?.notificationEmail || defaultValues?.agencyEmail || '',
      agentName: defaultValues?.agentName || '',
      agencyContactPerson: defaultValues?.agencyContactPerson || '',
      agencyEmail: defaultValues?.agencyEmail || '',
      clinicName: defaultValues?.clinicName || '',
      userContactName: defaultValues?.userContactName || '',
      userContactEmail: defaultValues?.userContactEmail || '',
    },
  });


  useEffect(() => {
    if (defaultValues?.notificationEmail) {
      setValue('notificationEmail', defaultValues.notificationEmail);
    }
  }, [defaultValues?.notificationEmail, setValue]);

  const emailValue = watch('userContactEmail');
  const notificationEmailValue = watch('notificationEmail');

  useEffect(() => {
    if (notificationEmailValue) {
      trigger('userContactEmail');
      trigger('notificationEmail');
    }
  }, [notificationEmailValue, trigger]);

  const handleEmailBlur = async () => {
    const isValid = await trigger('userContactEmail');

    const baseOptions = [
      {
        value: defaultValues?.agencyEmail || '',
        label: defaultValues?.agencyEmail || '',
      },
      ...initialEmailOptions,
    ].filter((option) => option.value);

    const newOptions = [...baseOptions];

    if (emailValue && isValid) {
      newOptions.push({ value: emailValue, label: emailValue });
    }

    const uniqueOptions = newOptions.filter(
      (v, i, a) => a.findIndex((t) => t.value === v.value) === i
    );

    setEmailOptions(uniqueOptions);
    await trigger('notificationEmail');
  };

  return {
    control,
    errors,
    emailOptions,
    handleEmailBlur,
    setValue,
    trigger,
    watch,
  };
};